import { SeeTopReasonInQuantifyButton } from '@modules/feed/components/SeeTopReasonInQuantifyButton';
import { useGetFeedDuration, useGetFeedWhereClause } from '@modules/feed/hooks';
import { FeedbackSummaryGlobalToggle } from '@modules/feedback/components/FeedbackList/FeedbackSummaryGlobalToggle';
import { SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW } from '@modules/feedback/constants';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { useGetFeedbackList } from '@modules/feedback/hooks/useGetFeedbackList';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import { FeedbackListContainer } from '@modules/feedbackV2/common/components/FeedbackListContainer';
import { FeedbackListHeaderContainer } from '@modules/feedbackV2/common/components/FeedbackListHeaderContainer';
import {
  useIsFeedbackListRedesignEnabled,
  useIsFeedbackRedesignEnabled,
} from '@modules/feedbackV2/common/hooks';
import { Box, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TimeUnit } from 'generated/graphql';
import Locker from 'lockr';
import { useMemo, useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import useQueryInspector from 'v2/core/services/queryBuilder/useQueryInspector';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { GenerateSummaryButton } from '../GenerateSummaryButton';
import { SearchAnalysesFeedbackListHeader } from './SearchAnalysesFeedbackListHeader';

interface SearchAnalysesFeedbackListProps {
  askOrSummarizeCTAProps: {
    isLoading: boolean;
    isVisible: boolean;
    onClick: () => void;
  };
}

export function SearchAnalysesFeedbackList({
  askOrSummarizeCTAProps,
}: SearchAnalysesFeedbackListProps) {
  const duration = useGetFeedDuration();
  const whereClause = useGetFeedWhereClause();

  const [isSummaryView, setIsSummaryView] = useState<boolean>(
    Locker.get(SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW) ?? false,
  );
  const [totalRecordCount, setTotalRecordCount] = useState<number>();

  const queryContainer = useMemo(
    () => ({
      AggregatePeriod: TimeUnit.Week,
      DateRange: duration as TimeRange,
      WhereClause: whereClause,
    }),
    [whereClause, duration],
  );

  const isFeedbackListRedesignEnabled = useIsFeedbackListRedesignEnabled();
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const getFeedbackListResp = useGetFeedbackList({
    queryContainer,
    shouldSkip: isFeedbackListRedesignEnabled, // no need to call this, API integration will be different for feedback redesign
  });

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: queryContainer?.WhereClause,
  });

  const handleSummaryViewChange = (isSummaryView: boolean) => {
    setIsSummaryView(isSummaryView);
  };

  const reasonFilters = useQueryInspector(
    queryContainer.WhereClause ?? EmptyWhereClause,
    FieldNames.Reason,
  );

  const isSmallScreen = useMediaQuery(
    `(max-width:${isFeedbackRedesignEnabled ? 1100 : 1020}px)`,
  );

  return (
    <Stack direction="column" spacing={2}>
      <Box
        sx={{
          mb: '-53px !important',
          ml: 'auto !important',
          pr: isSmallScreen ? '110px' : '250px',
        }}
      >
        <SeeTopReasonInQuantifyButton
          shouldShowCooccurringDataPoints={reasonFilters.length > 0}
          variant="text"
        />
      </Box>
      <FeedbackSummaryGlobalToggle
        isSummaryView={isSummaryView}
        onChange={handleSummaryViewChange}
        showShrinkedButton={isSmallScreen}
      />
      <FeedbackListHeaderContainer isSummaryView={isSummaryView}>
        <Stack direction="row" alignItems="center" spacing={2} height="100%">
          <SearchAnalysesFeedbackListHeader
            totalFeedbacks={
              isFeedbackListRedesignEnabled
                ? totalRecordCount
                : getFeedbackListResp.hits
            }
            isSummaryView={isSummaryView}
          />
          {askOrSummarizeCTAProps.isVisible && (
            <GenerateSummaryButton
              isLoading={askOrSummarizeCTAProps.isLoading}
              onClick={askOrSummarizeCTAProps.onClick}
            />
          )}
        </Stack>
      </FeedbackListHeaderContainer>
      <FeedbackListStoreProvider>
        <FeedbackListContainer
          selectedFeedbackMetadata={selectedFeedbackMetadata}
          queryContainer={queryContainer}
          queryString=""
          isSummaryView={isSummaryView}
          {...getFeedbackListResp}
          onFirstPageFetch={(resp) => setTotalRecordCount(resp.hits)}
        />
      </FeedbackListStoreProvider>
    </Stack>
  );
}
